import { useCallback, useEffect, useRef, useState } from "react";
import "./coachVoice.css";

// Voz del Entrenador de XIMA y del análisis del replay: lee en voz alta
// (speechSynthesis) el texto que se pinta en pantalla. No depende de la
// redacción: observa el DOM del contenedor y lee lo que haya.

const GENDER_KEY = "xima_coach_voice_gender";
// Velocidad guardada por voz (hombre y mujer tienen la suya).
const RATE_KEYS = { male: "xima_coach_voice_rate_male", female: "xima_coach_voice_rate_female" };
const LEGACY_RATE_KEY = "xima_coach_voice_rate";
// Velocidad por defecto de cada voz si el usuario aún no eligió una.
export const DEFAULT_COACH_RATES = { male: 1.25, female: 1 };
// Velocidades que va recorriendo la pastilla (0,5×–2×).
export const COACH_VOICE_RATES = [1, 1.25, 1.5, 1.75, 2, 0.5, 0.75];
// Las voces «Natural/Online» de Edge suenan mal muy rápido: tope.
const NATURAL_MAX_RATE = 1.75;

// Orden de preferencia de voces en español (por nombre).
const VOICE_PREFERENCES = {
  male: [
    /alvaro.*(natural|online)/i,
    /jorge.*(natural|online)/i,
    /gerardo.*(natural|online)/i,
    /tomas.*(natural|online)/i,
    /lorenzo.*(natural|online)/i,
    /gonzalo.*(natural|online)/i,
    /(natural|online).*(alvaro|jorge|gerardo|tomas|lorenzo|gonzalo|emilio|andres|mateo|sergio|rodrigo|carlos|luciano|federico|alex|victor|manuel|saul|arnau)/i,
    /microsoft pablo/i,
    /microsoft raul/i,
    /\bjorge\b/i,
    /\bjuan\b/i,
    /\bdiego\b/i,
    /\bcarlos\b/i,
    /\b(eddy|reed|rocko|grandpa)\b/i,
    /google.*(male|hombre|masculin)/i,
    /\b(male|hombre|masculin)/i,
  ],
  female: [
    /elvira.*(natural|online)/i,
    /dalia.*(natural|online)/i,
    /ximena.*(natural|online)/i,
    /paloma.*(natural|online)/i,
    /salome.*(natural|online)/i,
    /elena.*(natural|online)/i,
    /(natural|online).*(elvira|dalia|ximena|paloma|salome|elena|catalina|abril|estrella|irene|laia|lia|triana|vera|beatriz|candela|carlota|larissa|marina|nuria|renata|camila|sofia|valentina|yolanda|andrea|karla|maria|paola|tania|marta|belkys|lorena|emilia)/i,
    /microsoft (helena|laura|sabina)/i,
    /\b(m[oó]nica|paulina|marisol|ang[eé]lica|soledad|isabela)\b/i,
    /google/i,
    /\b(female|mujer|femenin)/i,
  ],
};
const FALLBACK_PITCH = { male: 0.85, female: 1.1 };

export const isCoachVoiceSupported = () =>
  typeof window !== "undefined" &&
  "speechSynthesis" in window &&
  typeof window.SpeechSynthesisUtterance === "function";

const readStored = (key, fallback) => {
  try {
    const value = window.localStorage.getItem(key);
    return value === null ? fallback : value;
  } catch (error) {
    return fallback;
  }
};

const store = (key, value) => {
  try {
    window.localStorage.setItem(key, value);
  } catch (error) {
    /* sin almacenamiento */
  }
};

const chosen = {}; // gender -> { voice, pitch, exact }

const pickVoice = (gender) => {
  if (!isCoachVoiceSupported()) return null;
  const voices = window.speechSynthesis.getVoices() || [];
  const spanish = voices.filter((voice) => /^es([-_]|$)/i.test(voice.lang || ""));
  if (!spanish.length) return null;
  for (const pattern of VOICE_PREFERENCES[gender]) {
    const voice = spanish.find((item) => pattern.test(item.name || ""));
    if (voice) return { voice, pitch: 1, exact: true };
  }
  const fallback =
    spanish.find((voice) => /es[-_]es/i.test(voice.lang) && voice.localService) ||
    spanish.find((voice) => /es[-_](es|mx|us)/i.test(voice.lang)) ||
    spanish[0];
  return { voice: fallback, pitch: FALLBACK_PITCH[gender], exact: false };
};

const ensureVoice = (gender) => {
  if (!chosen[gender]) {
    chosen[gender] = pickVoice(gender);
    const picked = chosen[gender];
    if (picked) {
      console.info(
        `[CoachVoice] voz ${gender === "female" ? "de mujer" : "de hombre"}: ${picked.voice.name} (${picked.voice.lang})${
          picked.exact ? "" : ` — no hay voz de ese tipo, pitch ${picked.pitch}`
        }`
      );
    }
  }
  return chosen[gender];
};

if (isCoachVoiceSupported()) {
  const refresh = () => {
    delete chosen.male;
    delete chosen.female;
  };
  try {
    window.speechSynthesis.addEventListener("voiceschanged", refresh);
  } catch (error) {
    window.speechSynthesis.onvoiceschanged = refresh;
  }
}

// Convierte el texto mostrado en algo que suene bien.
// Números de casilla en palabras completas («C1» → «C uno», nunca «C un»; libro4).
const CELL_NUMBER_WORDS = ["cero", "uno", "dos", "tres", "cuatro", "cinco", "seis", "siete", "ocho", "nueve", "diez", "once", "doce", "trece"];
const cellNumberWord = (number) => CELL_NUMBER_WORDS[Number(number)] || String(Number(number));

export const toSpeechText = (raw) =>
  String(raw || "")
    .replace(/\p{Extended_Pictographic}|️|‍/gu, " ")
    .replace(/\s*(?:→|->|⇒|➜|➝)\s*/g, " a ")
    // «domino» (de dominar) se leía «dominó»: acento en la i solo para la voz.
    .replace(/\b([Dd])omino\b/g, "$1omíno")
    .replace(/\b([A-Za-z])(\d{1,2})\b/g, (match, letter, number) => ` ${letter.toUpperCase()} ${cellNumberWord(number)} `)
    .replace(/[*_#~^|<>[\]{}=+✓✔✗✘•·«»]+/g, " ")
    .replace(/\s+([,.;:!?])/g, "$1")
    .replace(/\s{2,}/g, " ")
    .trim();

// Trozos cortos (Chrome corta las frases de más de ~15 s).
const splitSentences = (text) => {
  const parts = text.match(/[^.!?;]+[.!?;]*/g) || [text];
  const out = [];
  parts.forEach((part) => {
    let rest = part.trim();
    while (rest.length > 180) {
      const cut = rest.lastIndexOf(",", 180) > 60 ? rest.lastIndexOf(",", 180) + 1 : rest.lastIndexOf(" ", 180);
      const at = cut > 0 ? cut : 180;
      out.push(rest.slice(0, at).trim());
      rest = rest.slice(at).trim();
    }
    if (rest) out.push(rest);
  });
  return out;
};

// Mientras habla (y un momento después) el control por voz del replay ignora
// lo que oye, para no «escucharse» a sí mismo.
let quietUntil = 0;
let pendingUtterances = 0;

export const isCoachVoiceSpeaking = () =>
  isCoachVoiceSupported() &&
  (pendingUtterances > 0 || window.speechSynthesis.speaking || Date.now() < quietUntil);

let lastSpeakAt = 0;

// Promesa que se cumple cuando la voz terminó de leer (o en maxMs como tope).
// Sirve para que el play del replay no pase de jugada a mitad de frase.
// Chrome a veces no dispara onend: si speechSynthesis ya no habla ni tiene
// nada en cola, se da por terminada aunque falte algún onend.
export const waitForCoachVoiceIdle = ({ maxMs = 30000, isCancelled = () => false } = {}) =>
  new Promise((resolve) => {
    if (!isCoachVoiceSupported()) {
      resolve();
      return;
    }
    const startedAt = Date.now();
    const check = () => {
      if (isCancelled()) {
        resolve();
        return;
      }
      const synth = window.speechSynthesis;
      const engineIdle = !synth.speaking && !synth.pending;
      const now = Date.now();
      const done =
        (pendingUtterances === 0 && engineIdle) || (engineIdle && now - lastSpeakAt > 1500);
      if (done || now - startedAt >= maxMs) {
        resolve();
        return;
      }
      setTimeout(check, 150);
    };
    check();
  });

// Cada lectura nueva sube la generación: los onend de lo cancelado no cuentan.
let speechGeneration = 0;
let currentText = "";
let liveUtterances = []; // referencia para que el GC de Safari no las corte
let delayedSpeak = null;
let delayedText = "";

export const stopCoachVoice = () => {
  if (!isCoachVoiceSupported()) return;
  speechGeneration += 1;
  pendingUtterances = 0;
  currentText = "";
  liveUtterances = [];
  if (delayedSpeak) {
    clearTimeout(delayedSpeak);
    delayedSpeak = null;
  }
  window.speechSynthesis.cancel();
};

// Solo Chrome/Edge de escritorio cortan frases largas (~15 s): ahí se trocea.
// En iPhone/iPad, Safari y Android se manda el texto entero de una vez.
const shouldSplitSentences = () => {
  if (typeof navigator === "undefined") return true;
  const ua = navigator.userAgent || "";
  const isIOS =
    /iP(hone|ad|od)/.test(ua) || (/Macintosh/.test(ua) && Number(navigator.maxTouchPoints) > 1);
  if (isIOS || /Android|Mobile/i.test(ua)) return false;
  return /Chrome\/|Chromium\/|Edg\//.test(ua);
};

// Safari/iOS pierde el speak() que va justo detrás de un cancel(): esperar un poco.
const needsDelayAfterCancel = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return !/Chrome\/|Chromium\/|Edg\/|Firefox\//.test(ua) || /iP(hone|ad|od)/.test(ua);
};

let voiceGender = null;
const genderListeners = new Set();

const getGender = () => {
  if (voiceGender === null) voiceGender = readStored(GENDER_KEY, "male") === "female" ? "female" : "male";
  return voiceGender;
};

const VARIANT_KEY = "xima_coach_voice_variant";
let voiceVariant = null;
const getVariant = () => {
  if (voiceVariant === null) voiceVariant = readStored(VARIANT_KEY, "1") === "2" ? 1 : 0;
  return voiceVariant;
};

const voiceRates = {}; // gender -> rate
const rateListeners = new Set();

const getRate = (gender = getGender()) => {
  if (voiceRates[gender] === undefined) {
    let stored = readStored(RATE_KEYS[gender], null);
    // La velocidad única de antes cuenta como elegida para la voz que estaba seleccionada.
    if (stored === null && gender === getGender()) {
      const legacy = readStored(LEGACY_RATE_KEY, null);
      if (legacy !== null) {
        stored = legacy;
        store(RATE_KEYS[gender], legacy);
        try {
          window.localStorage.removeItem(LEGACY_RATE_KEY); // migrada una sola vez
        } catch (error) {
          /* sin almacenamiento */
        }
      }
    }
    const value = Number(stored);
    voiceRates[gender] = stored !== null && COACH_VOICE_RATES.includes(value) ? value : DEFAULT_COACH_RATES[gender];
  }
  return voiceRates[gender];
};

const notifyRate = () => {
  const rate = getRate();
  rateListeners.forEach((fn) => fn(rate));
};

const setRateShared = (rate) => {
  const gender = getGender();
  voiceRates[gender] = rate;
  store(RATE_KEYS[gender], String(rate));
  notifyRate();
};

// Velocidad de los bots que comentan (−/+ en la partida): la misma preferencia guardada
// que la del Entrenador; cambia la de las dos voces (hombre y mujer) a la vez.
const BOT_RATE_STEPS = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];
export const getBotVoiceRate = () => getRate("male");
export const stepBotVoiceRate = (direction) => {
  const current = getRate("male");
  let index = BOT_RATE_STEPS.findIndex((value) => value >= current - 0.001);
  if (index < 0) index = BOT_RATE_STEPS.length - 1;
  const nextIndex = Math.max(0, Math.min(BOT_RATE_STEPS.length - 1, index + (direction > 0 ? 1 : -1)));
  const next = BOT_RATE_STEPS[nextIndex];
  ["male", "female"].forEach((gender) => {
    voiceRates[gender] = next;
    store(RATE_KEYS[gender], String(next));
  });
  notifyRate();
  return next;
};
export const subscribeCoachRate = (fn) => {
  rateListeners.add(fn);
  return () => rateListeners.delete(fn);
};

export const formatCoachRate = (rate) => `${String(rate).replace(".", ",")}×`;

let lastSpoken = { text: "", at: 0 };

export const speakCoach = (raw, { force = false } = {}) => {
  if (!isCoachVoiceSupported()) return;
  const text = toSpeechText(raw);
  const now = Date.now();
  const synth = window.speechSynthesis;
  const stillSpeaking = pendingUtterances > 0 && (synth.speaking || synth.pending || delayedSpeak);
  // Dos tarjetas (panel y aviso) o un re-render pueden pedir lo mismo: no repetir.
  if (!force && text && text === lastSpoken.text && (now - lastSpoken.at < 1500 || stillSpeaking)) return;
  lastSpoken = { text, at: now };
  if (!text) {
    stopCoachVoice();
    return;
  }
  const gender = getGender();
  const variantVoice = getVariant() === 1 ? voiceForProfile(gender, 1) : null;
  const picked = variantVoice ? variantVoice.picked : ensureVoice(gender);
  const wantedRate = getRate() * (variantVoice?.picked?.rateFactor || 1);
  const rate =
    picked && /natural|online/i.test(picked.voice.name || "") ? Math.min(wantedRate, NATURAL_MAX_RATE) : wantedRate;

  // El mensaje solo creció (llegó la explicación del libro): seguir leyendo el resto
  // en vez de empezar otra vez desde la primera frase.
  let toSay = text;
  const extending = !force && stillSpeaking && currentText && text.startsWith(currentText);
  if (extending && delayedSpeak) {
    // Aún no empezó (pausa tras cancel en Safari): leerá el texto completo nuevo.
    delayedText = text;
    currentText = text;
    return;
  }
  if (extending) {
    toSay = text.slice(currentText.length).trim();
    if (!toSay) return;
  } else {
    const hadSpeech = synth.speaking || synth.pending || pendingUtterances > 0;
    stopCoachVoice();
    lastSpoken = { text, at: now };
    if (hadSpeech && needsDelayAfterCancel()) {
      // Se cuenta ya como «hablando» para que el play del replay espere.
      const generation = speechGeneration;
      pendingUtterances = 1;
      currentText = text;
      delayedText = text;
      lastSpeakAt = Date.now();
      delayedSpeak = setTimeout(() => {
        delayedSpeak = null;
        if (generation !== speechGeneration) return;
        pendingUtterances = 0;
        queueUtterances(delayedText, { picked, gender, rate });
      }, 120);
      return;
    }
  }
  currentText = text;
  queueUtterances(toSay, { picked, gender, rate });
};

const queueUtterances = (text, { picked, gender, rate }) => {
  const synth = window.speechSynthesis;
  const generation = speechGeneration;
  const parts = shouldSplitSentences() ? splitSentences(text) : [text];
  console.info(`[CoachVoice] speak (rate ${rate}, ${parts.length} trozo${parts.length === 1 ? "" : "s"}):`, text);
  lastSpeakAt = Date.now();
  parts.forEach((sentence) => {
    const utterance = new window.SpeechSynthesisUtterance(sentence);
    if (picked) {
      utterance.voice = picked.voice;
      utterance.lang = picked.voice.lang;
      utterance.pitch = picked.pitch;
    } else {
      utterance.lang = "es-ES";
      utterance.pitch = FALLBACK_PITCH[gender];
    }
    utterance.rate = rate;
    const done = () => {
      if (generation !== speechGeneration) return;
      pendingUtterances = Math.max(0, pendingUtterances - 1);
      quietUntil = Date.now() + 700;
      liveUtterances = liveUtterances.filter((item) => item !== utterance);
    };
    utterance.onend = done;
    utterance.onerror = done;
    pendingUtterances += 1;
    liveUtterances.push(utterance);
    synth.speak(utterance);
  });
};

// ---- Voces de los bots («Comentar partida de los bots», botscomentan1-20261025) ----
// Cada bot tiene su voz: puesto 0 = hombre 1, puesto 1 = mujer 1, puesto 2 = hombre 2,
// puesto 3 = mujer 2. Si no hay una segunda voz distinta, la misma con otro tono
// (hombre 2 más grave, mujer 2 más aguda). La velocidad es la elegida para esa voz.
export const BOT_VOICE_PROFILES = [
  { gender: "male", variant: 0 },
  { gender: "female", variant: 0 },
  { gender: "male", variant: 1 },
  { gender: "female", variant: 1 },
];
const SECOND_VOICE_TUNE = { male: { pitch: 0.72, rate: 0.95 }, female: { pitch: 1.3, rate: 1.06 } };
const botVoices = {}; // gender -> [{ voice, pitch, exact }]

const pickVoiceList = (gender) => {
  if (!isCoachVoiceSupported()) return [];
  const voices = window.speechSynthesis.getVoices() || [];
  const spanish = voices.filter((voice) => /^es([-_]|$)/i.test(voice.lang || ""));
  const list = [];
  VOICE_PREFERENCES[gender].forEach((pattern) => {
    spanish.forEach((voice) => {
      if (pattern.test(voice.name || "") && !list.includes(voice)) list.push(voice);
    });
  });
  return list;
};

// «male1» | «male2» | «female1» | «female2» ↔ { gender, variant }.
export const BOT_VOICE_IDS = ["male1", "female1", "male2", "female2"];
export const voiceIdOf = (gender, variant) => `${gender === "female" ? "female" : "male"}${variant === 1 ? 2 : 1}`;
export const parseVoiceId = (id) => {
  const match = /^(male|female)([12])$/.exec(String(id || ""));
  return match ? { gender: match[1], variant: match[2] === "2" ? 1 : 0 } : null;
};
// Voz por defecto de cada puesto (sin elección): 0 hombre 1, 1 mujer 1, 2 hombre 2, 3 mujer 2.
export const defaultBotVoiceId = (slot) => {
  const profile = BOT_VOICE_PROFILES[((Number(slot) % 4) + 4) % 4];
  return voiceIdOf(profile.gender, profile.variant);
};

const voiceForProfile = (gender, variant) => {
  if (!botVoices[gender] || !botVoices[gender].length) {
    const list = pickVoiceList(gender);
    const first = list[0] ? { voice: list[0], pitch: 1, rateFactor: 1 } : ensureVoice(gender);
    // Segunda voz: otra persona de verdad si la hay; si no, la primera con otro tono.
    const other = list.find((voice) => first && voice !== first.voice && voice.name !== first.voice.name);
    const tune = SECOND_VOICE_TUNE[gender];
    const second = other
      ? { voice: other, pitch: 1, rateFactor: 1 }
      : first
      ? { voice: first.voice, pitch: (first.pitch || 1) * tune.pitch, rateFactor: tune.rate }
      : null;
    botVoices[gender] = [first ? { rateFactor: 1, ...first } : null, second];
  }
  return { gender, picked: botVoices[gender][variant] || botVoices[gender][0] };
};

// slot (número) o voz («female2»).
const botVoiceFor = (slotOrVoice) => {
  const parsed = parseVoiceId(slotOrVoice) || parseVoiceId(defaultBotVoiceId(Number(slotOrVoice) || 0));
  return voiceForProfile(parsed.gender, parsed.variant);
};

if (isCoachVoiceSupported()) {
  try {
    window.speechSynthesis.addEventListener("voiceschanged", () => {
      delete botVoices.male;
      delete botVoices.female;
    });
  } catch (error) {
    /* sin evento */
  }
}

let botSpeechGeneration = 0;
let botLiveUtterances = [];

export const stopBotVoices = () => {
  if (!isCoachVoiceSupported()) return;
  botSpeechGeneration += 1;
  botLiveUtterances = [];
  window.speechSynthesis.cancel();
};

// Lee una frase con la voz del bot del puesto `slot`. Se cumple al terminar de leer
// (con un tope por si el navegador no avisa del final). Nunca corta lo que ya suena:
// quien la llama espera a que termine la anterior.
export const speakBotLine = (raw, slot) =>
  new Promise((resolve) => {
    if (!isCoachVoiceSupported()) {
      resolve();
      return;
    }
    const text = toSpeechText(raw);
    if (!text) {
      resolve();
      return;
    }
    const synth = window.speechSynthesis;
    const { gender, picked } = botVoiceFor(slot);
    const wantedRate = getRate(gender) * (picked?.rateFactor || 1);
    const rate =
      picked && /natural|online/i.test(picked.voice?.name || "") ? Math.min(wantedRate, NATURAL_MAX_RATE) : wantedRate;
    const generation = botSpeechGeneration;
    const parts = shouldSplitSentences() ? splitSentences(text) : [text];
    let left = parts.length;
    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      clearTimeout(guard);
      clearInterval(idleCheck);
      quietUntil = Date.now() + 500;
      resolve();
    };
    // Tope: unos 90 ms por letra a velocidad 1 y 4 s de margen.
    const guard = setTimeout(finish, Math.round((text.length * 90) / Math.max(0.5, rate)) + 4000);
    const startedAt = Date.now();
    // Chrome a veces no dispara onend: si ya no habla nada, se da por terminada.
    const idleCheck = setInterval(() => {
      if (generation !== botSpeechGeneration) finish();
      else if (Date.now() - startedAt > 1500 && !synth.speaking && !synth.pending) finish();
    }, 250);
    lastSpeakAt = Date.now();
    parts.forEach((sentence) => {
      const utterance = new window.SpeechSynthesisUtterance(sentence);
      if (picked && picked.voice) {
        utterance.voice = picked.voice;
        utterance.lang = picked.voice.lang;
        utterance.pitch = Math.min(2, Math.max(0, picked.pitch || 1));
      } else {
        utterance.lang = "es-ES";
        utterance.pitch = FALLBACK_PITCH[gender];
      }
      utterance.rate = rate;
      const done = () => {
        botLiveUtterances = botLiveUtterances.filter((item) => item !== utterance);
        left -= 1;
        if (left <= 0) finish();
      };
      utterance.onend = done;
      utterance.onerror = done;
      botLiveUtterances.push(utterance);
      synth.speak(utterance);
    });
  });

const setGenderShared = (gender) => {
  getRate(); // fija (y migra) la velocidad de la voz que se deja
  voiceGender = gender;
  store(GENDER_KEY, gender);
  genderListeners.forEach((fn) => fn(gender));
  notifyRate(); // la pastilla muestra la velocidad de la voz nueva
};

// Voz del Entrenador elegida: «male1» | «male2» | «female1» | «female2» (la pastilla
// Hombre/Mujer y el selector de «Jugar con bot» usan las mismas claves).
export const getCoachVoiceId = () => voiceIdOf(getGender(), getVariant());
export const setCoachVoiceId = (id) => {
  const parsed = parseVoiceId(id);
  if (!parsed) return;
  voiceVariant = parsed.variant;
  store(VARIANT_KEY, parsed.variant === 1 ? "2" : "1");
  setGenderShared(parsed.gender);
};
export const subscribeCoachVoice = (fn) => {
  const listener = () => fn(getCoachVoiceId());
  genderListeners.add(listener);
  return () => genderListeners.delete(listener);
};

// Estado encendido/apagado compartido por clave (entrenador y replay por separado).
const onState = {};
const onListeners = {};

const getOn = (storageKey) => {
  if (onState[storageKey] === undefined) onState[storageKey] = readStored(storageKey, "0") === "1";
  return onState[storageKey];
};

const setOnShared = (storageKey, on) => {
  onState[storageKey] = on;
  store(storageKey, on ? "1" : "0");
  (onListeners[storageKey] || new Set()).forEach((fn) => fn(on));
};

// Texto legible de un nodo: los trozos (título, comentario) separados por punto.
const readNodeText = (node) => {
  if (!node) return "";
  const children = Array.from(node.children || []).filter((child) => !/^(IMG|SVG|BUTTON)$/i.test(child.tagName));
  const hasOwnText = Array.from(node.childNodes || []).some(
    (child) => child.nodeType === 3 && child.textContent.trim()
  );
  // Texto con negritas dentro de la frase: se lee tal cual.
  if (!children.length || hasOwnText) return (node.textContent || "").trim();
  return children
    .map(readNodeText)
    .filter(Boolean)
    .map((part) => (/[.!?:;…]$/.test(part) ? part : `${part}.`))
    .join(" ");
};

// Guion de los bots: cada trozo con la voz de su bot, uno detrás de otro.
let botScriptRun = 0;
export const speakBotSegments = (segments) => {
  stopCoachVoice();
  stopBotVoices();
  botScriptRun += 1;
  const run = botScriptRun;
  (async () => {
    for (const segment of segments) {
      if (run !== botScriptRun) return;
      const voice = parseVoiceId(segment?.voice) ? segment.voice : "male1";
      // eslint-disable-next-line no-await-in-loop
      await speakBotLine(String(segment?.text || ""), voice);
    }
  })();
};
const readScriptSegments = (node) => {
  try {
    const raw = node && node.getAttribute && node.getAttribute("data-bot-script");
    const segments = raw ? JSON.parse(raw) : null;
    return Array.isArray(segments) && segments.length ? segments : null;
  } catch (error) {
    return null;
  }
};

const TRAINER_OPTIONS = {
  container: ".g7-trainer",
  text: ".g7-trainer-body",
  idle: ".is-idle",
  storageKey: "xima_coach_voice_on",
  who: "del entrenador",
};

// Bocina (y selector Hombre/Mujer) para poner dentro de la tarjeta que se lee.
export function CoachVoiceButton({
  container = TRAINER_OPTIONS.container,
  text = TRAINER_OPTIONS.text,
  idle = TRAINER_OPTIONS.idle,
  storageKey = TRAINER_OPTIONS.storageKey,
  who = TRAINER_OPTIONS.who,
}) {
  const supported = isCoachVoiceSupported();
  const [on, setOn] = useState(() => supported && getOn(storageKey));
  const [gender, setGender] = useState(getGender);
  const [rate, setRate] = useState(getRate);
  const wrapRef = useRef(null);
  const onRef = useRef(on);
  onRef.current = on;

  useEffect(() => {
    if (!onListeners[storageKey]) onListeners[storageKey] = new Set();
    const set = onListeners[storageKey];
    set.add(setOn);
    genderListeners.add(setGender);
    rateListeners.add(setRate);
    return () => {
      set.delete(setOn);
      genderListeners.delete(setGender);
      rateListeners.delete(setRate);
    };
  }, [storageKey]);

  const readCurrent = useCallback(() => {
    const card = wrapRef.current && wrapRef.current.closest(container);
    const node = card && card.querySelector(text);
    return { node, value: readNodeText(node) };
  }, [container, text]);

  useEffect(() => {
    if (!supported) return undefined;
    const card = wrapRef.current && wrapRef.current.closest(container);
    if (!card) return undefined;
    let lastText = readCurrent().value;
    const check = () => {
      const { node, value } = readCurrent();
      if (value === lastText) return;
      lastText = value;
      if (!onRef.current) return;
      if (!node || (idle && node.matches(idle))) {
        stopCoachVoice();
        return;
      }
      const segments = readScriptSegments(node);
      if (segments) speakBotSegments(segments);
      else speakCoach(value);
    };
    const observer = new MutationObserver(check);
    observer.observe(card, { childList: true, subtree: true, characterData: true });
    return () => observer.disconnect();
  }, [supported, container, idle, readCurrent]);

  useEffect(() => () => stopCoachVoice(), []);

  const toggle = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      const next = !onRef.current;
      setOnShared(storageKey, next);
      const current = readCurrent();
      const segments = next ? readScriptSegments(current.node) : null;
      if (segments) speakBotSegments(segments);
      else if (next) speakCoach(current.value, { force: true });
      else {
        stopCoachVoice();
        stopBotVoices();
      }
    },
    [storageKey, readCurrent]
  );

  const switchGender = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      setGenderShared(getGender() === "female" ? "male" : "female");
      // Que se oiga la voz nueva con el mensaje actual.
      speakCoach(readCurrent().value, { force: true });
    },
    [readCurrent]
  );

  const switchRate = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      const index = COACH_VOICE_RATES.indexOf(getRate());
      setRateShared(COACH_VOICE_RATES[(index + 1) % COACH_VOICE_RATES.length]);
      // Que se oiga la velocidad nueva con el mensaje actual.
      speakCoach(readCurrent().value, { force: true });
    },
    [readCurrent]
  );

  if (!supported) return null;
  const nextRate = COACH_VOICE_RATES[(COACH_VOICE_RATES.indexOf(rate) + 1) % COACH_VOICE_RATES.length];
  const label = on ? `Silenciar voz ${who}` : `Activar voz ${who}`;
  const isFemale = gender === "female";
  return (
    <span ref={wrapRef} className={`coach-voice${on ? " is-on" : ""}`}>
      {on ? (
        <button
          type="button"
          className="coach-voice-gender"
          onClick={switchGender}
          title={isFemale ? "Voz de mujer (pulsa para voz de hombre)" : "Voz de hombre (pulsa para voz de mujer)"}
          aria-label={isFemale ? "Voz de mujer. Cambiar a voz de hombre" : "Voz de hombre. Cambiar a voz de mujer"}
        >
          {isFemale ? "Mujer" : "Hombre"}
        </button>
      ) : null}
      {on ? (
        <button
          type="button"
          className="coach-voice-rate"
          onClick={switchRate}
          title={`Velocidad ${formatCoachRate(rate)} (pulsa para ${formatCoachRate(nextRate)})`}
          aria-label={`Velocidad de la voz ${formatCoachRate(rate)}. Cambiar a ${formatCoachRate(nextRate)}`}
        >
          {formatCoachRate(rate)}
        </button>
      ) : null}
      <button
        type="button"
        className={`coach-voice-btn${on ? " is-on" : ""}`}
        onClick={toggle}
        title={label}
        aria-label={label}
        aria-pressed={on}
      >
        <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
          <path d="M4 9v6h4l5 4V5L8 9H4z" fill="currentColor" />
          {on ? (
            <>
              <path d="M16 8.5a5 5 0 0 1 0 7" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              <path d="M18.5 6a8.5 8.5 0 0 1 0 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </>
          ) : (
            <path d="M16 9l5 6M21 9l-5 6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          )}
        </svg>
      </button>
    </span>
  );
}

// Bocina del análisis del replay: lee la valoración y el comentario de la jugada actual.
export function ReplayReviewVoiceButton() {
  return (
    <CoachVoiceButton
      container=".r7-review"
      text=".r7-review-current span"
      idle=""
      storageKey="xima_replay_voice_on"
      who="del análisis"
    />
  );
}

export default CoachVoiceButton;
